import { useMemo } from "react";
import isEqual from "lodash/isEqual";

import { PlanningFilters } from "@landtechnologies/search-layer-contracts";
import { PlanningStatus, planningStatuses } from "react-migration/domains/planning/types";
import { useLocalStorageReducer } from "react-migration/lib/persistence/state/useLocalStorageReducer";
import { planningTypes } from "react-migration/domains/planning/constants";

export const defaultPlanningFilterState: PlanningFilters = {
  showResidential: true,
  showCommercial: true,
  showOther: true,
  showBoundaries: true,
  maxYear: 5,
  minSize: 1,
  enabledTypes: planningTypes.map((planningType) => planningType.id).sort(),
  enabledStatuses: planningStatuses.map((x) => x.id).sort(),
  description: "",
  showAppealed: false,
};

const noFilters = {
  ...defaultPlanningFilterState,
  maxYear: 35,
  minSize: 0,
};

type Action =
  | { type: "SET_RESIDENTIAL"; value: boolean }
  | { type: "SET_COMMERCIAL"; value: boolean }
  | { type: "SET_SHOW_BOUNDARIES"; value: boolean }
  | { type: "SET_OTHER"; value: boolean }
  | { type: "SET_YEARS"; value: number }
  | { type: "SET_HOMES"; value: number }
  | { type: "SET_ENABLED_TYPES"; value: string[] }
  | { type: "SET_ENABLED_STATUSES"; value: PlanningStatus[] }
  | { type: "SET_DESCRIPTION"; value: string }
  | { type: "SET_SHOW_APPEALED"; value: boolean }
  | { type: "RESET_STATE" }
  | { type: "REMOVE_ALL" };

function createReducer(initialState: PlanningFilters) {
  return function reducer(state: PlanningFilters, action: Action): PlanningFilters {
    switch (action.type) {
      case "SET_RESIDENTIAL":
        return { ...state, showResidential: action.value };
      case "SET_COMMERCIAL":
        return { ...state, showCommercial: action.value };
      case "SET_OTHER":
        return { ...state, showOther: action.value };
      case "SET_HOMES":
        return { ...state, minSize: action.value };
      case "SET_YEARS":
        return { ...state, maxYear: action.value };
      case "SET_ENABLED_TYPES":
        return { ...state, enabledTypes: action.value };
      case "SET_ENABLED_STATUSES":
        return { ...state, enabledStatuses: action.value };
      case "SET_SHOW_BOUNDARIES":
        return { ...state, showBoundaries: action.value };
      case "SET_DESCRIPTION":
        return { ...state, description: action.value };
      case "SET_SHOW_APPEALED":
        return { ...state, showAppealed: action.value };
      case "RESET_STATE":
        return { ...initialState };
      case "REMOVE_ALL":
        return { ...noFilters };
    }
  };
}

export type PlanningState = ReturnType<typeof usePlanningFilterState>[0];
export type PlanningStateDispatch = ReturnType<typeof usePlanningFilterState>[1];

export function usePlanningFilterState(localStorageKey: string, initialStatePartial: Partial<PlanningFilters> = {}) {
  const initialState = useMemo(
    () => ({
      ...defaultPlanningFilterState,
      ...initialStatePartial,
    }),
    [initialStatePartial]
  );

  const [state, dispatch] = useLocalStorageReducer(localStorageKey, createReducer(initialState), initialState);

  const hasDefaultFilters = useMemo(() => {
    return isEqual(initialState, state);
  }, [initialState, state]);

  const hasNoFilters = useMemo(() => {
    return isEqual(noFilters, state);
  }, [state]);

  return [state, dispatch, { hasDefaultFilters, hasNoFilters }] as const;
}
